import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Organization, OrgHat, OrgPolicy, OrgPart } from '@newgenus/common';
import { Component, Inject, OnInit, OnDestroy, inject } from '@angular/core';
import { HatLocationPipe } from '../../shared/hat-location.pipe';
import { OrgViewToggleComponent, OrgListViewService } from '../../shared/org-view-toggle';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MaterialModule } from '../../../material.module';
import { OrgHatDetailsDialogComponent } from '../org-hat-details-dialog';
import { OrgHatExplorerDialogComponent } from '../org-hat-explorer-dialog/org-hat-explorer-dialog';
import { OrgHatCleanupDialogComponent } from '../org-hat-cleanup-dialog/org-hat-cleanup-dialog';
import { OrgDialogService } from '../../services/org-dialog.service';
import { OrgEntityColorsService } from '../../../org-dashboard/services/org-entity-colors.service';
import { OrgFilterBarComponent, OrgFilterConfig } from '../../shared/org-filter-bar/org-filter-bar.component';
import { OrgSearchBarComponent, PositionSearchResult } from '../../shared/org-search-bar/org-search-bar.component';
import { fuzzyMatch } from '../../shared/org-filter-utils';
import { OrgBoardService } from '../../services/org-board-service';
import { Subject, takeUntil } from 'rxjs';
import { restrictToKeys } from '../../shared/only-keys';

export interface HatsListDialogData {
    organization: Organization;
    positionFilter?: string;
    /** Only these items (a staff member's dashboard tile, ISS-022); absent = all. */
    onlyKeys?: string[];
}

interface HatDisplayCard {
    key: string;
    name: string;
    description: string;
    userCount: number;
    securityGroupCount: number;
    positionCount: number;
    policyCount: number;
    featureCount: number;
    permissionCount: number;
    isMultiUsers: boolean;
    isSpecialRights: boolean;
    isPostHat: boolean;
    positionNames: string[];
    userNames: string[];
    sgNames: string[];
    policyNames: string[];
    orgHat: OrgHat;
}

@Component({
    selector: 'shared-org-hats-list-dialog',
    imports: [
        HatLocationPipe,
        MaterialModule,
        CommonModule,
        FormsModule,
        OrgFilterBarComponent,
        OrgSearchBarComponent,
        OrgViewToggleComponent,
    ],
    template: `
        <h2 mat-dialog-title class="dialog-header">
            <mat-icon class="mr-2">school</mat-icon>
            Organization Hats ({{ filteredHats.length }} of {{ allHats.length }})

            <button
                mat-icon-button
                class="explorer-button"
                (click)="openExplorer()"
                matTooltip="Open Hat Explorer - View hierarchical tree of org parts and hats"
            >
                <mat-icon>account_tree</mat-icon>
            </button>

            @if (orgDialogService.mayManageHats) {
                <button
                    mat-icon-button
                    class="cleanup-button"
                    (click)="openCleanup()"
                    matTooltip="Open Hat Cleanup — find and bulk-delete unused hats or remove stale user assignments"
                >
                    <mat-icon>cleaning_services</mat-icon>
                </button>
            }

            <shared-org-view-toggle></shared-org-view-toggle>
            <button mat-icon-button mat-dialog-close class="close-button">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="dialog-content" [class.ng-list-view]="(listView.mode$ | async) === 'list'">
            <!-- Search Bar -->
            <shared-org-search-bar
                searchLabel="Search hats"
                searchPlaceholder="Search by name, staff member, group, policy..."
                [showPositionSearch]="true"
                [positionNames]="allPositionNames"
                [initialPositionTerm]="positionSearchTerm"
                (searchChanged)="onSearchChanged($event)"
                (positionSearchChanged)="onPositionSearchChanged($event)"
            >
            </shared-org-search-bar>

            <!-- Filters -->
            <shared-org-filter-bar
                [filters]="filterConfig"
                [rightFilters]="rightFilterConfig"
                activeColor="#1093ad"
                activeDarkColor="#26c6da"
                [totalCount]="allHats.length"
                (filtersChanged)="onFiltersChanged($event)"
            >
            </shared-org-filter-bar>

            <!-- Hat card template (reused in both views) -->
            <ng-template #hatCardTpl let-hat>
                <mat-card
                    class="hat-card"
                    [class.special-rights-card]="hat.isSpecialRights"
                    [class.post-card]="hat.isPostHat && !hat.isSpecialRights"
                    (dblclick)="onHatDoubleClick(hat)"
                >
                    <div class="card-identity">
                        <div
                            class="entity-avatar"
                            [ngStyle]="{
                                background:
                                    'linear-gradient(135deg, ' +
                                    entityColors.getColor('hats') +
                                    ' 0%, ' +
                                    entityColors.getColor('hats') +
                                    'cc 100%)',
                            }"
                        >
                            <mat-icon class="entity-icon">school</mat-icon>
                        </div>
                        <div class="identity-text">
                            <span class="entity-name">{{ hat.name | titlecase }}</span>
                            <span
                                class="ng-hat-location"
                                *ngIf="hat.key | hatLocation: data.organization as hatWhere"
                                >{{ hatWhere }}</span
                            >
                            <span class="entity-subtitle" *ngIf="hat.description">
                                {{ hat.description | slice: 0 : 50 }}{{ hat.description.length > 50 ? '...' : '' }}
                            </span>
                        </div>
                        <div class="multi-user-badge" *ngIf="hat.isMultiUsers" matTooltip="Multi-Staff hat">
                            <mat-icon>group</mat-icon>
                        </div>
                        <div class="special-rights-badge" *ngIf="hat.isSpecialRights" matTooltip="Special Rights hat">
                            <mat-icon>shield</mat-icon>
                        </div>
                    </div>
                    <div class="card-footer">
                        <div class="badges-row">
                            <span
                                class="badge"
                                matTooltip="Staff assigned"
                                [ngStyle]="
                                    hat.userCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('users'),
                                              color: entityColors.getColor('users'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">person</mat-icon>
                                {{ hat.userCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Security groups"
                                [ngStyle]="
                                    hat.securityGroupCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('securityGroups'),
                                              color: entityColors.getColor('securityGroups'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">security</mat-icon>
                                {{ hat.securityGroupCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Org Parts"
                                [ngStyle]="
                                    hat.positionCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('positions'),
                                              color: entityColors.getColor('positions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">place</mat-icon>
                                {{ hat.positionCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Policies"
                                [ngStyle]="
                                    hat.policyCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('policies'),
                                              color: entityColors.getColor('policies'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">policy</mat-icon>
                                {{ hat.policyCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Features"
                                [ngStyle]="
                                    hat.featureCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('features'),
                                              color: entityColors.getColor('features'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">extension</mat-icon>
                                {{ hat.featureCount }}
                            </span>
                            <span
                                class="badge"
                                matTooltip="Permissions"
                                [ngStyle]="
                                    hat.permissionCount > 0
                                        ? {
                                              'background-color': entityColors.getBackground('permissions'),
                                              color: entityColors.getColor('permissions'),
                                          }
                                        : null
                                "
                            >
                                <mat-icon class="badge-icon">vpn_key</mat-icon>
                                {{ hat.permissionCount }}
                            </span>
                        </div>
                        <div class="card-actions">
                            <button
                                mat-icon-button
                                matTooltip="View Details"
                                color="primary"
                                (click)="onViewHat(hat); $event.stopPropagation()"
                            >
                                <mat-icon>visibility</mat-icon>
                            </button>
                            <button
                                mat-icon-button
                                matTooltip="Edit Hat"
                                color="accent"
                                (click)="onEditHat(hat); $event.stopPropagation()"
                            >
                                <mat-icon>edit</mat-icon>
                            </button>
                        </div>
                    </div>
                </mat-card>
            </ng-template>

            <!-- Grouped by Position view -->
            <ng-container *ngIf="groupByPosition">
                <div *ngFor="let group of positionGroups" class="position-group">
                    <div
                        class="group-header"
                        [ngStyle]="{ 'border-left-color': entityColors.getColor('positions') }"
                        (click)="togglePositionCollapse(group.position)"
                    >
                        <mat-icon class="collapse-icon" [class.collapsed]="collapsedPositions.has(group.position)"
                            >expand_more</mat-icon
                        >
                        <mat-icon [ngStyle]="{ color: entityColors.getColor('positions') }">work</mat-icon>
                        <span class="group-name">{{ group.position }}</span>
                        <span class="group-count">({{ group.hats.length }})</span>
                    </div>
                    <div class="hats-grid" *ngIf="!collapsedPositions.has(group.position)">
                        <ng-container *ngFor="let hat of group.hats">
                            <ng-container *ngTemplateOutlet="hatCardTpl; context: { $implicit: hat }"></ng-container>
                        </ng-container>
                    </div>
                </div>
                <div *ngIf="positionGroups.length === 0" class="empty-state">
                    <mat-icon class="empty-icon">account_tree</mat-icon>
                    <p>No org part groups to display</p>
                </div>
            </ng-container>

            <!-- Normal (ungrouped) view -->
            <div class="hats-grid" *ngIf="!groupByPosition && filteredHats.length > 0">
                <ng-container *ngFor="let hat of filteredHats">
                    <ng-container *ngTemplateOutlet="hatCardTpl; context: { $implicit: hat }"></ng-container>
                </ng-container>
            </div>

            <!-- Empty State -->
            <div *ngIf="!groupByPosition && filteredHats.length === 0" class="empty-state">
                <mat-icon class="empty-icon">school</mat-icon>
                <p *ngIf="searchTerm">No hats found matching "{{ searchTerm }}"</p>
                <p
                    *ngIf="
                        !searchTerm &&
                        (noUsersFilter ||
                            multiUserFilter ||
                            noPoliciesFilter ||
                            noSgFilter ||
                            noPositionsFilter ||
                            specialRightsFilter ||
                            postsFilter)
                    "
                >
                    No hats match the active filters
                </p>
                <p
                    *ngIf="
                        !searchTerm &&
                        !noUsersFilter &&
                        !multiUserFilter &&
                        !noPoliciesFilter &&
                        !noSgFilter &&
                        !noPositionsFilter &&
                        !specialRightsFilter &&
                        !postsFilter
                    "
                >
                    No hats in this organization
                </p>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button mat-dialog-close>Close</button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            .dialog-header {
                display: flex;
                align-items: center;
                gap: 8px;
                margin: 0;
                padding: 16px 24px;
                border-bottom: 1px solid rgba(0, 0, 0, 0.12);
            }

            .explorer-button {
                margin-left: 8px;
                color: var(--entity-color-hats, #1093ad);
            }

            .explorer-button:hover {
                background-color: rgba(16, 147, 173, 0.1);
            }

            .cleanup-button {
                margin-left: 8px;
                color: #f57c00;
            }

            .cleanup-button:hover {
                background-color: rgba(245, 124, 0, 0.1);
            }

            .close-button {
                margin-left: 4px;
            }

            .dialog-content {
                padding: 16px 24px;
                min-height: 400px;
                max-height: 70vh;
                overflow-y: auto;
            }

            .w-100 {
                width: 100%;
            }

            .hats-grid {
                display: grid;
                grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                gap: 16px;
            }

            .hat-card {
                cursor: pointer;
                transition:
                    transform 0.2s,
                    box-shadow 0.2s,
                    border-color 0.2s;
                border: 1px solid rgba(0, 0, 0, 0.08);
                display: flex;
                flex-direction: column;
                padding: 12px 16px;
                background-color: #f5f5f5;
            }

            .hat-card:hover {
                transform: translateY(-2px);
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                border-color: rgba(0, 0, 0, 0.25);
            }

            .hat-card.special-rights-card {
                background-color: rgba(156, 39, 176, 0.08);
                border-color: rgba(156, 39, 176, 0.2);
            }

            .hat-card.special-rights-card:hover {
                border-color: rgba(156, 39, 176, 0.4);
            }

            .hat-card.post-card {
                background-color: rgba(33, 150, 243, 0.08);
                border-color: rgba(33, 150, 243, 0.2);
            }

            .hat-card.post-card:hover {
                border-color: rgba(33, 150, 243, 0.4);
            }

            .card-identity {
                display: flex;
                align-items: center;
                gap: 12px;
                margin-bottom: 12px;
            }

            .entity-avatar {
                width: 40px;
                height: 40px;
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-shrink: 0;
            }

            .entity-icon {
                color: white;
                font-size: 22px;
                width: 22px;
                height: 22px;
            }

            .identity-text {
                display: flex;
                flex-direction: column;
                overflow: hidden;
                min-width: 0;
                flex: 1;
            }

            .entity-name {
                font-size: 15px;
                font-weight: 600;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .entity-subtitle {
                font-size: 12px;
                color: rgba(0, 0, 0, 0.55);
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .card-footer {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 8px;
                border-top: 1px solid rgba(0, 0, 0, 0.06);
                padding-top: 8px;
            }

            .badges-row {
                display: flex;
                flex-wrap: wrap;
                gap: 4px;
                min-width: 0;
            }

            .card-actions {
                display: flex;
                flex-shrink: 0;
                align-items: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
            }

            .card-actions ::ng-deep .mat-mdc-icon-button .mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            .badge {
                display: inline-flex;
                align-items: center;
                gap: 2px;
                padding: 2px 6px;
                border-radius: 12px;
                font-size: 12px;
                background-color: rgba(0, 0, 0, 0.06);
                color: rgba(0, 0, 0, 0.5);
            }

            .badge-icon {
                font-size: 14px;
                width: 14px;
                height: 14px;
            }

            .multi-user-badge {
                flex-shrink: 0;
                color: var(--entity-color-users, #1976d2);
            }

            .special-rights-badge {
                flex-shrink: 0;
                color: #9c27b0;
            }

            .multi-user-badge mat-icon,
            .special-rights-badge mat-icon {
                font-size: 18px;
                width: 18px;
                height: 18px;
            }

            /* Position group layout */
            .position-group {
                margin-bottom: 24px;
            }

            .group-header {
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 8px 12px;
                margin-bottom: 12px;
                border-left: 3px solid;
                background: rgba(0, 0, 0, 0.03);
                border-radius: 0 4px 4px 0;
                font-size: 14px;
                font-weight: 500;
                cursor: pointer;
                user-select: none;
            }

            .group-header:hover {
                background: rgba(0, 0, 0, 0.06);
            }

            .collapse-icon {
                transition: transform 0.2s;
                color: rgba(0, 0, 0, 0.4);
                font-size: 20px;
                width: 20px;
                height: 20px;
            }

            .collapse-icon.collapsed {
                transform: rotate(-90deg);
            }

            .group-name {
                font-weight: 600;
            }

            .group-count {
                color: rgba(0, 0, 0, 0.45);
                font-weight: 400;
            }

            .empty-state {
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 48px;
                color: rgba(0, 0, 0, 0.54);
            }

            .empty-icon {
                font-size: 64px;
                width: 64px;
                height: 64px;
                margin-bottom: 16px;
            }

            .mr-2 {
                margin-right: 8px;
            }

            /* Dark theme overrides */
            :host-context(.dark-theme) .hat-card {
                background-color: #383838;
                border-color: rgba(255, 255, 255, 0.1);
            }
            :host-context(.dark-theme) .hat-card:hover {
                box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
                border-color: rgba(255, 255, 255, 0.2);
            }
            :host-context(.dark-theme) .hat-card.special-rights-card {
                background-color: rgba(156, 39, 176, 0.15);
                border-color: rgba(156, 39, 176, 0.3);
            }
            :host-context(.dark-theme) .hat-card.special-rights-card:hover {
                border-color: rgba(156, 39, 176, 0.5);
            }
            :host-context(.dark-theme) .hat-card.post-card {
                background-color: rgba(33, 150, 243, 0.15);
                border-color: rgba(33, 150, 243, 0.3);
            }
            :host-context(.dark-theme) .hat-card.post-card:hover {
                border-color: rgba(33, 150, 243, 0.5);
            }
            :host-context(.dark-theme) .entity-subtitle {
                color: rgba(255, 255, 255, 0.55);
            }
            :host-context(.dark-theme) .card-footer {
                border-top-color: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .badge {
                background-color: rgba(255, 255, 255, 0.1);
                color: rgba(255, 255, 255, 0.5);
            }
            :host-context(.dark-theme) .group-header {
                background: rgba(255, 255, 255, 0.04);
            }
            :host-context(.dark-theme) .group-header:hover {
                background: rgba(255, 255, 255, 0.08);
            }
            :host-context(.dark-theme) .collapse-icon {
                color: rgba(255, 255, 255, 0.4);
            }
            :host-context(.dark-theme) .group-count {
                color: rgba(255, 255, 255, 0.45);
            }
            :host-context(.dark-theme) .empty-state {
                color: rgba(255, 255, 255, 0.54);
            }

            /* Phones: full-screen dialog (global .ob-list-dialog) → tighten chrome and
           drop the 280px-min grid to a single full-width column. */
            @media (max-width: 767.98px) {
                .dialog-header {
                    padding: 12px 16px;
                    font-size: 18px;
                }
                .dialog-content {
                    padding: 12px 16px;
                    min-height: 0;
                }
                /* minmax(0,1fr) (not 1fr) removes the implicit min-content floor so the
               card actually shrinks to the viewport instead of overflowing right. */
                .hats-grid {
                    grid-template-columns: minmax(0, 1fr);
                    gap: 12px;
                }
                .hats-grid > * {
                    min-width: 0;
                    max-width: 100%;
                }
                .card-identity,
                .identity-text {
                    min-width: 0;
                }
                /* Badges take a full row (wrapping internally), actions drop below. */
                .card-footer {
                    flex-wrap: wrap;
                }
                .badges-row {
                    flex: 1 1 100%;
                }
                .card-actions {
                    margin-left: auto;
                }
            }
        `,
    ],
})
export class OrgHatsListDialogComponent implements OnInit, OnDestroy {
    public entityColors = inject(OrgEntityColorsService);
    public listView = inject(OrgListViewService);
    private obService = inject(OrgBoardService);
    private destroy$ = new Subject<void>();
    public searchTerm = '';
    public positionSearchTerm = '';
    public matchedPositionNames: string[] = [];
    public allHats: HatDisplayCard[] = [];
    public filteredHats: HatDisplayCard[] = [];
    public allPositionNames: string[] = [];

    // Filter configs for shared filter bar
    public filterConfig: OrgFilterConfig[] = [];
    public rightFilterConfig: OrgFilterConfig[] = [];

    // Filters (kept for internal filterHats logic)
    public noUsersFilter = false;
    public multiUserFilter = false;
    public noPoliciesFilter = false;
    public noUsersCount = 0;
    public multiUserCount = 0;
    public noPoliciesCount = 0;
    public noSgFilter = false;
    public noSgCount = 0;
    public noPositionsFilter = false;
    public noPositionsCount = 0;
    public specialRightsFilter = false;
    public specialRightsCount = 0;
    public postsFilter = false;
    public postsCount = 0;

    // Position grouping
    public groupByPosition = false;
    public positionGroups: { position: string; hats: HatDisplayCard[] }[] = [];
    public collapsedPositions = new Set<string>();

    constructor(
        public dialogRef: MatDialogRef<OrgHatsListDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: HatsListDialogData,
        private dialog: MatDialog,
        public orgDialogService: OrgDialogService
    ) {}

    public ngOnInit(): void {
        console.log('OrgHatsListDialogComponent > ngOnInit > data:', this.data);
        console.log('OrgHatsListDialogComponent > ngOnInit > organization:', this.data?.organization);
        console.log('OrgHatsListDialogComponent > ngOnInit > orgHats:', this.data?.organization?.orgHats);
        if (this.data?.positionFilter) {
            this.positionSearchTerm = this.data.positionFilter;
        }
        this.loadHats();
        this.subscribeToOrgUpdates();
    }

    public ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Subscribe to live organization updates so the dialog refreshes when hats change */
    private subscribeToOrgUpdates(): void {
        this.obService.organization$.pipe(takeUntil(this.destroy$)).subscribe((org) => {
            if (!org) return;
            this.data.organization = org;
            this.loadHats();
        });
    }

    private loadHats(): void {
        const org = this.data.organization;
        console.log('loadHats > org:', org);
        console.log('loadHats > orgHats:', org?.orgHats);
        console.log('loadHats > hatsKeys:', org?.orgHats?.hatsKeys);

        if (!org?.orgHats?.hatsKeys) {
            console.warn('No hatsKeys found on organization.orgHats');
            this.allHats = [];
            this.filteredHats = [];
            return;
        }

        // Pre-calculate policy counts per hat from orgPolicies (source of truth)
        const policyCountByHat = new Map<string, number>();
        const orgPolicies = org.orgPolicies || {};
        for (const [, policyData] of Object.entries(orgPolicies)) {
            const policy = policyData as OrgPolicy | undefined;
            if (policy?.status === 'published' && policy.targets?.resolvedHatKeys) {
                for (const hatKey of policy.targets.resolvedHatKeys) {
                    policyCountByHat.set(hatKey, (policyCountByHat.get(hatKey) || 0) + 1);
                }
            }
        }

        this.allHats = restrictToKeys(org.orgHats.hatsKeys as string[], this.data.onlyKeys, (k) => k).map(
            (hatKey: string) => {
                const orgHat = org.orgHats[hatKey] as OrgHat;
                console.log('Processing hat:', hatKey, orgHat);

                const userCount = orgHat?.assignedToArray?.length || orgHat?.userKeys?.length || 0;
                const securityGroupCount = orgHat?.securityGroupsArray?.length || 0;
                const positionCount = orgHat?.positionArray?.length || orgHat?.positions?.length || 0;
                const policyCount = policyCountByHat.get(hatKey) || 0;

                // Compute features and permissions from the hat's security groups
                const orgSecurityGroups = org.orgSecurityGroups || ({} as any);
                const hatSgArray = orgHat?.securityGroupsArray || [];
                const features = new Set<string>();
                let permissionCount = 0;
                for (const sg of hatSgArray) {
                    const sgKey = typeof sg === 'string' ? sg : (sg as any)?.key;
                    if (sgKey) {
                        const sgData = orgSecurityGroups[sgKey];
                        if (sgData?.features && Array.isArray(sgData.features)) {
                            sgData.features.forEach((f: string) => features.add(f));
                        }
                    }
                }
                // Permissions: count unique permissions across all features in the hat's security groups
                for (const sgItem of hatSgArray) {
                    const sgKey = typeof sgItem === 'string' ? sgItem : (sgItem as any)?.key;
                    if (sgKey) {
                        const sgData = orgSecurityGroups[sgKey];
                        if (sgData?.featureDetails) {
                            Object.values(sgData.featureDetails).forEach((fd: any) => {
                                if (fd?.permissions && Array.isArray(fd.permissions)) {
                                    permissionCount += fd.permissions.length;
                                }
                            });
                        }
                    }
                }

                return {
                    key: hatKey,
                    name: orgHat?.name || 'Unnamed Hat',
                    description: orgHat?.description || '',
                    userCount,
                    securityGroupCount,
                    positionCount,
                    policyCount,
                    featureCount: features.size,
                    permissionCount,
                    isMultiUsers: orgHat?.isMultiUsers || orgHat?.multiUsers || false,
                    isSpecialRights: orgHat?.isSpecialRights || false,
                    isPostHat: this.isHatLinkedToPost(orgHat, org),
                    positionNames: this.resolvePositionNames(orgHat, org),
                    userNames: this.resolveUserNames(orgHat, org),
                    sgNames: this.resolveSecurityGroupNames(orgHat, org),
                    policyNames: this.resolvePolicyNames(hatKey, org),
                    orgHat,
                };
            }
        );

        console.log('loadHats > allHats:', this.allHats);
        // Sort by name
        this.allHats.sort((a, b) => a.name.localeCompare(b.name));
        this.allPositionNames = this.getAllPositionNames();
        this.computeFilterCounts();
        this.filterHats();
    }

    private computeFilterCounts(): void {
        this.noUsersCount = this.allHats.filter((h) => h.userCount === 0).length;
        this.multiUserCount = this.allHats.filter((h) => h.isMultiUsers).length;
        this.noPoliciesCount = this.allHats.filter((h) => h.policyCount === 0).length;
        this.noSgCount = this.allHats.filter((h) => h.securityGroupCount === 0).length;
        this.noPositionsCount = this.allHats.filter((h) => h.positionCount === 0).length;
        this.postsCount = this.allHats.filter((h) => h.isPostHat).length;
        this.specialRightsCount = this.allHats.filter((h) => h.isSpecialRights).length;
        this.buildFilterConfigs();
    }

    private buildFilterConfigs(): void {
        this.filterConfig = [
            { key: 'noUsers', label: 'No Staff', icon: 'person_off', count: this.noUsersCount },
            { key: 'multiUser', label: 'Multi-User', icon: 'group', count: this.multiUserCount },
            { key: 'noPolicies', label: 'No Policies', icon: 'policy', count: this.noPoliciesCount },
            { key: 'noSg', label: 'No SG', icon: 'shield', count: this.noSgCount },
            { key: 'noPositions', label: 'No Org Parts', icon: 'wrong_location', count: this.noPositionsCount },
            { key: 'posts', label: 'Posts', icon: 'work', count: this.postsCount },
            { key: 'specialRights', label: 'Special Rights', icon: 'shield', count: this.specialRightsCount },
        ];
        this.rightFilterConfig = [{ key: 'groupByPosition', label: 'By Org Part', icon: 'account_tree' }];
    }

    public onSearchChanged(term: string): void {
        this.searchTerm = term;
        this.filterHats();
    }

    public onPositionSearchChanged(result: PositionSearchResult): void {
        this.positionSearchTerm = result.term;
        this.matchedPositionNames = result.matchedNames;
        this.filterHats();
    }

    public onFiltersChanged(activeFilters: Set<string>): void {
        this.noUsersFilter = activeFilters.has('noUsers');
        this.multiUserFilter = activeFilters.has('multiUser');
        this.noPoliciesFilter = activeFilters.has('noPolicies');
        this.noSgFilter = activeFilters.has('noSg');
        this.noPositionsFilter = activeFilters.has('noPositions');
        this.postsFilter = activeFilters.has('posts');
        this.specialRightsFilter = activeFilters.has('specialRights');

        // Handle groupByPosition toggle
        const newGroupByPosition = activeFilters.has('groupByPosition');
        if (newGroupByPosition !== this.groupByPosition) {
            this.groupByPosition = newGroupByPosition;
            if (!this.groupByPosition) {
                this.collapsedPositions.clear();
            }
        }

        this.filterHats();
    }

    public filterHats(): void {
        let result = [...this.allHats];

        // Text search across name, description, users, security groups, policies, positions
        if (this.searchTerm.trim()) {
            const term = this.searchTerm.toLowerCase();
            result = result.filter(
                (hat) =>
                    hat.name.toLowerCase().includes(term) ||
                    (hat.description && hat.description.toLowerCase().includes(term)) ||
                    hat.positionNames.some((p) => p.toLowerCase().includes(term)) ||
                    hat.userNames.some((u) => u.toLowerCase().includes(term)) ||
                    hat.sgNames.some((sg) => sg.toLowerCase().includes(term)) ||
                    hat.policyNames.some((p) => p.toLowerCase().includes(term))
            );
        }

        // Position search (fuzzy) - uses shared fuzzyMatch
        if (this.positionSearchTerm.trim()) {
            const posTerm = this.positionSearchTerm.toLowerCase().trim();
            result = result.filter((hat) => hat.positionNames.some((pos) => fuzzyMatch(pos.toLowerCase(), posTerm)));
        }

        // Toggle filters
        if (this.noUsersFilter) {
            result = result.filter((h) => h.userCount === 0);
        }
        if (this.multiUserFilter) {
            result = result.filter((h) => h.isMultiUsers);
        }
        if (this.noPoliciesFilter) {
            result = result.filter((h) => h.policyCount === 0);
        }
        if (this.noSgFilter) {
            result = result.filter((h) => h.securityGroupCount === 0);
        }
        if (this.noPositionsFilter) {
            result = result.filter((h) => h.positionCount === 0);
        }
        if (this.postsFilter) {
            result = result.filter((h) => h.isPostHat);
        }
        if (this.specialRightsFilter) {
            result = result.filter((h) => h.isSpecialRights);
        }

        this.filteredHats = result;

        if (this.groupByPosition) {
            this.buildPositionGroups();
        }
    }

    private getAllPositionNames(): string[] {
        const names = new Set<string>();
        for (const hat of this.allHats) {
            for (const pos of hat.positionNames) {
                names.add(pos);
            }
        }
        return Array.from(names).sort();
    }

    public togglePositionCollapse(position: string): void {
        if (this.collapsedPositions.has(position)) {
            this.collapsedPositions.delete(position);
        } else {
            this.collapsedPositions.add(position);
        }
    }

    private buildPositionGroups(): void {
        const groupMap = new Map<string, HatDisplayCard[]>();

        this.filteredHats.forEach((hat) => {
            if (hat.positionNames.length === 0) {
                const key = 'No Org Part';
                if (!groupMap.has(key)) groupMap.set(key, []);
                groupMap.get(key)?.push(hat);
            } else {
                hat.positionNames.forEach((pos) => {
                    if (!groupMap.has(pos)) groupMap.set(pos, []);
                    groupMap.get(pos)?.push(hat);
                });
            }
        });

        this.positionGroups = Array.from(groupMap.entries())
            .map(([position, hats]) => ({ position, hats }))
            .sort((a, b) => {
                if (a.position === 'No Org Part') return 1;
                if (b.position === 'No Org Part') return -1;
                return a.position.localeCompare(b.position);
            });

        // Collapse all by default on first build
        if (this.collapsedPositions.size === 0 && this.positionGroups.length > 0) {
            this.positionGroups.forEach((g) => this.collapsedPositions.add(g.position));
        }
    }

    private resolvePositionNames(orgHat: OrgHat, org: Organization): string[] {
        const names: string[] = [];

        // Try positions array (objects with name)
        if (orgHat?.positions && Array.isArray(orgHat.positions)) {
            (orgHat.positions as Array<{ name?: string }>).forEach((pos) => {
                if (pos?.name && !names.includes(pos.name)) {
                    names.push(pos.name);
                }
            });
        }

        // Try positionArray (keys) - resolve names from org parts
        if (orgHat?.positionArray && Array.isArray(orgHat.positionArray)) {
            const orgParts = org.orgParts || [];
            orgHat.positionArray.forEach((partKey: string) => {
                const part = orgParts.find((p) => p.key === partKey || p.partKey === partKey);
                if (part?.name && !names.includes(part.name)) {
                    names.push(part.name);
                } else if (!part) {
                    console.warn(
                        `resolvePositionNames: hat "${orgHat?.name}" (${orgHat?.key}) has positionArray key "${partKey}" that does NOT exist in orgParts (${orgParts.length} parts). positions array:`,
                        orgHat?.positions
                    );
                }
            });
        }

        if (names.length === 0) {
            console.warn(
                `resolvePositionNames: hat "${orgHat?.name}" (${orgHat?.key}) resolved to ZERO position names. positions:`,
                orgHat?.positions,
                'positionArray:',
                orgHat?.positionArray
            );
        }

        return names;
    }

    /**
     * Checks if any of the hat's linked positions are posts.
     * Uses partType if explicitly set, otherwise infers from depth (6+ = post).
     * Units (depth 5) are containers and excluded — only actual posts count.
     */
    private isHatLinkedToPost(orgHat: OrgHat, org: Organization): boolean {
        if (!orgHat?.positionArray || !Array.isArray(orgHat.positionArray)) return false;
        const orgParts = org.orgParts || [];
        return orgHat.positionArray.some((partKey: string) => {
            const part = orgParts.find((p) => p.key === partKey || p.partKey === partKey);
            if (!part) return false;
            const resolved = this.resolvePartType(part);
            return resolved === 'post';
        });
    }

    /** Resolve part type: use partType if set, else infer from depth.
     *  Matches the logic in org-positions-list-dialog. */
    private resolvePartType(part: OrgPart): string {
        if (part.partType) return part.partType.toLowerCase();
        const depth = part.depth || 0;
        if (depth <= 1) return 'executive';
        if (depth === 2) return 'division';
        if (depth === 3) return 'department';
        if (depth === 4) return 'section';
        if (depth === 5) return 'unit';
        if (depth >= 6) return 'post';
        return '';
    }

    private resolveUserNames(orgHat: OrgHat, org: Organization): string[] {
        const userKeys: string[] = (orgHat as any)?.assignedToArray || orgHat?.userKeys || [];
        const orgUsers = org?.orgUsers;
        if (!orgUsers) return [];
        return userKeys.filter((k) => orgUsers[k]).map((k) => orgUsers[k].name || orgUsers[k].userName || '');
    }

    private resolveSecurityGroupNames(orgHat: OrgHat, org: Organization): string[] {
        const sgKeys: string[] = (orgHat?.securityGroupsArray || []).map((sg: any) =>
            typeof sg === 'string' ? sg : sg?.key
        );
        const orgSGs = org?.orgSecurityGroups;
        if (!orgSGs) return [];
        return sgKeys.filter((k) => orgSGs[k]).map((k) => orgSGs[k].name || '');
    }

    private resolvePolicyNames(hatKey: string, org: Organization): string[] {
        const names: string[] = [];
        const orgPolicies = org?.orgPolicies || {};
        const policiesKeys = (orgPolicies as any).policiesKeys || [];
        for (const policyKey of policiesKeys) {
            const policy = (orgPolicies as any)[policyKey] as OrgPolicy | undefined;
            if (policy?.status === 'published' && policy.targets?.resolvedHatKeys?.includes(hatKey)) {
                if (policy.name) names.push(policy.name);
            }
        }
        return names;
    }

    public onViewHat(hat: HatDisplayCard): void {
        // Open hat details dialog WITHOUT closing the hats list
        this.openHatDetailsDialog(hat);
    }

    public onEditHat(hat: HatDisplayCard): void {
        // TODO: Implement edit functionality
        console.log('Edit hat:', hat.name);
        // For now, just open details
        this.openHatDetailsDialog(hat);
    }

    public onHatDoubleClick(hat: HatDisplayCard): void {
        // Open hat details dialog WITHOUT closing the hats list
        this.openHatDetailsDialog(hat);
    }

    private openHatDetailsDialog(hat: HatDisplayCard): void {
        this.dialog.open(OrgHatDetailsDialogComponent, {
            data: {
                hat: hat.orgHat,
                hatKey: hat.key,
                organization: this.data.organization,
            },
            height: '85%',
            width: '85%',
            maxHeight: 'unset',
            maxWidth: 'unset',
        });
    }

    /**
     * Open the Hat Explorer dialog showing hierarchical trees
     */
    public openExplorer(): void {
        this.dialog.open(OrgHatExplorerDialogComponent, {
            data: {
                organization: this.data.organization,
            },
            width: '90%',
            height: '90vh',
            maxWidth: '1400px',
            maxHeight: '900px',
            panelClass: ['hat-explorer-dialog', 'ob-list-dialog'],
        });
    }

    /**
     * Open the Hat Cleanup dialog for bulk delete/user removal
     */
    public openCleanup(): void {
        const ref = this.dialog.open(OrgHatCleanupDialogComponent, {
            data: { organization: this.data.organization },
            width: '1200px',
            height: '80vh',
        });
        ref.afterClosed().subscribe(() => {
            // Refresh hats list after cleanup
            this.loadHats();
        });
    }
}
